import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { Traceable } from '@repo/shared/dtos/assistant'
import { generateId } from '@repo/shared/uuid/uuid'
import { DataStatus } from '@repo/ui/utils'

import { AsyncThunkConfig, RejectValue } from '@/store/actions'
import {
  CreateReportTemplate,
  ReportTemplate,
  UpdateReportTemplate,
} from '@/store/report-templates/types'

export const getReportTemplates = createAsyncThunk<
  { reportTemplates: ReportTemplate[] },
  { organizationId: string },
  AsyncThunkConfig
>(
  'GET_REPORT_TEMPLATES',
  async ({ organizationId }, { extra, fulfillWithValue, rejectWithValue }) => {
    const { altApi } = extra
    const res = await altApi.reportTemplates.getAll({ organizationId })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ reportTemplates: res })
  },
)

export const createReportTemplate = createAsyncThunk<
  { reportTemplate: ReportTemplate },
  { fields: CreateReportTemplate },
  AsyncThunkConfig
>(
  'CREATE_REPORT_TEMPLATE',
  async ({ fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { altApi } = extra
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await altApi.reportTemplates.createOne({
      organizationId,
      fields,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ reportTemplate: res })
  },
)

export const updateReportTemplate = createAsyncThunk<
  { reportTemplate: ReportTemplate },
  { fields: UpdateReportTemplate; reportTemplateId: string },
  AsyncThunkConfig
>(
  'UPDATE_REPORT_TEMPLATE',
  async ({ reportTemplateId, fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { altApi } = extra
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await altApi.reportTemplates.updateOne({
      organizationId,
      reportTemplateId,
      fields,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ reportTemplate: res })
  },
)

export const deleteReportTemplate = createAsyncThunk<
  { success: true },
  { reportTemplate: ReportTemplate },
  AsyncThunkConfig
>(
  'DELETE_REPORT_TEMPLATE',
  async ({ reportTemplate }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { altApi } = extra
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await altApi.reportTemplates.deleteOne({
      organizationId,
      reportTemplateId: reportTemplate.id,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const generateReport = createAsyncThunk<
  { report: string } & Traceable,
  { instructions: string },
  AsyncThunkConfig
>(
  'GENERATE_REPORT',
  async ({ instructions }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { api } = extra
    const state = getState()
    const user = state.user.me
    if (!DataStatus.isFulfilled(user)) return rejectWithValue(RejectValue.NoUser)
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const beneficiaryId = state.beneficiary.currentBeneficiaryId
    if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)

    const res = await api.beneficiaries.one.generateReport({
      beneficiaryId,
      generationId: generateId(),
      instructions,
      organizationId,
      userId: user.id,
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)
